import React, { useEffect, useRef, useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { toast } from "@/components/portal/Toast"
import type { ActionPlan, CatalogEntry, DraftDetail } from "./types"
import { UserPlus, UserCheck, Folder, FolderCheck, Calendar, Mail, Pencil, Eye, Search, X, ClipboardList, ListChecks } from "lucide-react"
import { SlotEditModal } from "./SlotEditModal"
import { CreateContactModal } from "@/components/portal/CreateContactModal"
import { ContactEditModal } from "@/components/portal/ContactEditModal"
import { EmailBody } from "@/components/portal/EmailBody"
import { AttachmentsSection } from "@/components/portal/AttachmentsSection"
import { markdownStrippen } from "@/lib/utils"
import { ohneIds } from "@/lib/ohneIds"
import { MitProjektLinks, useProjektNrLinks, type ProjektLinks } from "@/lib/projektNr"
import { ProaktivHerkunft } from "./proaktiv"


/** Eine Aktion aus dem Katalog, wie sie die kompakte Karte als Zeile zeigt. */
export type KartenAktion = {
  type: string
  /** Beschriftung aus dem Katalog (LABELS im Posteingang). */
  label: string
  /** Schon gelaufen — Zeile bleibt sichtbar, aber durchgestrichen. */
  fertig: boolean
  /** Angehakt. */
  an: boolean
  /** Ueberschreibt die aus dem Plan abgeleitete Detailzeile. */
  detail?: string
  /** Steuerung unter der Zeile — z.B. wer die Aufgabe bekommt. */
  zusatz?: React.ReactNode
  /** Kleine Marke vor dem Titel — „Aufgabe" oder „Logbuch".
   *  Neun der neunzehn Katalog-Aktionen legen in Wahrheit eine HERO-Aufgabe an
   *  und unterscheiden sich nur im Titel. Die Marke macht die Familie sichtbar,
   *  ohne dass zehn Zeilen dasselbe Wort tragen (Paul, 21.08.2026). */
  familie?: "Aufgabe" | "Logbuch"
}

/**
 * Agent-Plan-Card — Phase Agent-2: einzelne Felder inline editierbar.
 * Slot, Gewerk, Projekt-Typ, Kontakt-Name. Aenderungen via onPlanChange
 * an den ReviewCard zurueck (der haelt den editierten Plan-State).
 *
 * Zwei Darstellungen, ein Code:
 *
 * - **Review** (`kompakt=false`): die gewachsene Fassung mit Original-Mail,
 *   Sektionslabels und ausgeschriebenen Steps.
 * - **Posteingang** (`kompakt=true`): das Redesign — eine Karte, eine
 *   Rahmenebene, Aktionen als Checkbox-Zeilen mit je einer Detailzeile.
 *
 * Bewusst keine zweite Komponente: der erste Anlauf im Posteingang war ein
 * Eigenbau, und die beiden Oberflaechen liefen sofort auseinander.
 */
import { TaskCreateModal } from "@/components/portal/TaskCreateModal"
import { TerminlinkAktion } from "./TerminlinkAktion"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { useHeroVorgang } from "@/lib/heroVorgang"
import { SlotDetails, type SlotCheck } from "./SlotDetails"
import { AKTION_FELDER, AUFGABEN_AKTIONEN, FREIER_LOGBUCH_TEXT, label, paramZeile, schrittFuerAktion, vorschlagTitel, zeigtDetailsBlock } from "./aktionFelder"

export function ActionPlanCard({
  plan, detail, editable = false, onPlanChange, onBodyRefreshed,
  kompakt = false, aktionen, onToggleAktion, kopfExtra, hinweis, primaeraktion,
  nebenaktion, onAntwortAnsehen, weitereSlots, onToggleSlot, nachtragbar, terminUeberholt = false, karte = false,
}: {
  plan: ActionPlan
  detail: DraftDetail
  editable?: boolean
  onPlanChange?: (next: ActionPlan) => void
  /** Callback wenn der Reply-Body neu generiert wurde (z.B. nach Slot-Edit).
   *  ReviewCard nutzt das um den lokalen `draft`-State zu aktualisieren —
   *  ohne diesen Callback wuerde der Refresh nur in plan.reply.body landen,
   *  aber das Anzeigefeld zeigt `draft` (der state). */
  onBodyRefreshed?: (newBody: string) => void
  /** Kompakte Fassung fuer den Posteingang (Redesign). */
  kompakt?: boolean
  /** Neues Postfach: Satz, Begründung und Historie stehen schon darüber —
   *  die Karte zeigt nur noch die Schritte (Paul 28.10.: „doppelt drin“). */
  karte?: boolean
  /** Die Katalog-Aktionen als Checkbox-Zeilen. Nur im kompakten Modus. */
  aktionen?: KartenAktion[]
  /** Aktionen, die der Planer NICHT vorgesehen hat — ausgegraut zum Nachtragen.
   *  `fest` steht immer sichtbar (die Schritte, die einen festen Platz haben),
   *  `weitere` liegt hinter dem Aufklapper. Nur im kompakten Modus. */
  nachtragbar?: { fest: CatalogEntry[]; weitere: CatalogEntry[] }
  onToggleAktion?: (type: string) => void
  /** Rechts im Kartenkopf (Posteingang: der Historie-Badge). */
  kopfExtra?: React.ReactNode
  /** Zwischen Liste und Fuss (Terminvorschlaege, Verlauf-Schalter). */
  hinweis?: React.ReactNode
  /** Der Knopf in der Fusszeile. Den Rest der Zeile baut die Karte. */
  primaeraktion?: React.ReactNode
  /** Rechts aussen in der Fusszeile — Abstand zum Rest ist Absicht. */
  nebenaktion?: React.ReactNode
  /** "Ansehen" an der Antwort-Zeile — oeffnet den Entwurf weiter unten. */
  onAntwortAnsehen?: () => void
  /** Zusaetzliche Terminvorschlaege, einzeln abwaehlbar. */
  weitereSlots?: { key: "slot_secondary" | "slot_tertiary"; an: boolean; slot?: ActionPlan["slot"] }[]
  onToggleSlot?: (key: "slot_secondary" | "slot_tertiary") => void
  /** #197: Zur Mail steht schon ein Termin — die Terminvorschläge (Details und
   *  weitere Vorschläge) werden nicht gezeigt. */
  terminUeberholt?: boolean
}) {
  const [mailPopupOpen, setMailPopupOpen] = useState(false)
  const [belegeOffen, setBelegeOffen] = useState(false)
  const [openModal, setOpenModal] = useState<"contact" | "contact-edit" | "project" | null>(null)
  const [weitereOffen, setWeitereOffen] = useState(false)
  // Projekt-Daten des Plans inline bearbeiten (#84). Frueher oeffnete
  // „Bearbeiten" am Projekt-Schritt den HERO-Anlege-Dialog — der legte den
  // Vorgang SOFORT in HERO an, statt nur den Plan zu aendern; danach war
  // der Schritt „erledigt" und nicht mehr bearbeitbar (Marlene, 08.09.2026).
  const [projektEditOffen, setProjektEditOffen] = useState(false)
  const [slotEditKey, setSlotEditKey] = useState<"slot" | "slot_secondary" | "slot_tertiary" | null>(null)
  const [slotCheck, setSlotCheck] = useState<SlotCheck | null>(null)
  const [slotCheckSecondary, setSlotCheckSecondary] = useState<SlotCheck | null>(null)
  const [slotCheckTertiary, setSlotCheckTertiary] = useState<SlotCheck | null>(null)

  // Projektnummern im Fliesstext („… Projekt KLI-791 passt thematisch") werden
  // zu Links in den Vorgang. Aufgeloest wird gegen den Projekt-Cache — was es
  // nicht gibt, bleibt Text (in Mails stehen genug Nummern, die keine sind).
  const projektLinks = useProjektNrLinks(
    detail.tenant_slug,
    plan.reasoning,
    plan.monteur_task?.title,
    plan.monteur_task?.body,
  )

  const update = (patch: Partial<ActionPlan>) => onPlanChange?.({ ...plan, ...patch })

  // Body-Refresh nach manuellem Slot-Edit: LLM schreibt den Reply-Body neu
  // mit dem aktuellen Slot. Slot bleibt unveraendert (kein Re-Plan).
  const refreshBody = useMutation({
    mutationFn: async (planForBody: ActionPlan) => {
      const r = await fetch(`/api/v1/drafts/${detail.email_id}/refresh-body`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_plan: planForBody }),
      })
      if (!r.ok) {
        const txt = await r.text().catch(() => "")
        throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`)
      }
      return r.json() as Promise<{ body: string; subject: string }>
    },
    onSuccess: (d, planForBody) => {
      // Plan-Cache mit neuem Body updaten
      const nextReply = { ...(planForBody.reply || {}), body: d.body }
      onPlanChange?.({ ...planForBody, reply: nextReply as ActionPlan["reply"] })
      // Parent (ReviewCard) ueber den neuen Body informieren — der updatet den
      // lokalen draft-State, sonst sieht der User die alte Body-Variante.
      if (d.body) onBodyRefreshed?.(d.body)
      toast("✓ Antwort-Text an neuen Termin angepasst")
    },
    onError: (e) => toast("Body-Refresh fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })
  const rawStepEnabled = plan.step_enabled || { contact: true, project: true, slot: true, monteur_task: true, reply: true }
  // HERO-Constraint: ein Termin braucht ein project_match_id. Wenn Project-Step
  // deaktiviert ist UND es kein bestehendes Projekt gibt → Slot-Step zwangsweise
  // auch deaktivieren, sonst wuerde der Executor ihn silent skippen.
  const slotBlocked = !rawStepEnabled.project && !plan.project.id
  const stepEnabled = slotBlocked ? { ...rawStepEnabled, slot: false } : rawStepEnabled
  const toggleStep = (key: keyof typeof stepEnabled) => {
    // Slot-Toggle ignorieren wenn geblockt (kein Projekt → kein Termin moeglich)
    if (key === "slot" && slotBlocked) return
    update({ step_enabled: { ...rawStepEnabled, [key]: !rawStepEnabled[key] } as ActionPlan["step_enabled"] })
  }

  // Live-Check: Ist der vorgeschlagene Slot noch frei? + welche Monteure?
  // Refresht auch bei Monteur-Wechsel (partner_id) im Plan.
  useEffect(() => {
    setSlotCheck(null)
    const start = plan.slot.start_datetime
    if (!start) return
    const dur = plan.slot.duration_minutes || 45
    const pid = plan.slot.partner_id
    const params = new URLSearchParams({ start, duration_min: String(dur) })
    if (pid) params.set("partner_id", String(pid))
    const ctrl = new AbortController()
    fetch(
      `/api/v1/drafts/${detail.email_id}/slot-check?${params.toString()}`,
      { credentials: "include", signal: ctrl.signal },
    )
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (j) setSlotCheck(j) })
      .catch(() => { /* ignore */ })
    return () => ctrl.abort()
  }, [plan.slot.start_datetime, plan.slot.duration_minutes, plan.slot.partner_id, detail.email_id])

  // Live-Check fuer slot_secondary (zweiter Vorschlag, falls gesetzt).
  useEffect(() => {
    setSlotCheckSecondary(null)
    const start = plan.slot_secondary?.start_datetime
    if (!start) return
    const dur = plan.slot_secondary?.duration_minutes || 45
    const pid = plan.slot_secondary?.partner_id
    const params = new URLSearchParams({ start, duration_min: String(dur) })
    if (pid) params.set("partner_id", String(pid))
    const ctrl = new AbortController()
    fetch(
      `/api/v1/drafts/${detail.email_id}/slot-check?${params.toString()}`,
      { credentials: "include", signal: ctrl.signal },
    )
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (j) setSlotCheckSecondary(j) })
      .catch(() => { /* ignore */ })
    return () => ctrl.abort()
  }, [plan.slot_secondary?.start_datetime, plan.slot_secondary?.duration_minutes, plan.slot_secondary?.partner_id, detail.email_id])

  // Live-Check fuer slot_tertiary (dritter Vorschlag, falls gesetzt).
  useEffect(() => {
    setSlotCheckTertiary(null)
    const start = plan.slot_tertiary?.start_datetime
    if (!start) return
    const dur = plan.slot_tertiary?.duration_minutes || 45
    const pid = plan.slot_tertiary?.partner_id
    const params = new URLSearchParams({ start, duration_min: String(dur) })
    if (pid) params.set("partner_id", String(pid))
    const ctrl = new AbortController()
    fetch(
      `/api/v1/drafts/${detail.email_id}/slot-check?${params.toString()}`,
      { credentials: "include", signal: ctrl.signal },
    )
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (j) setSlotCheckTertiary(j) })
      .catch(() => { /* ignore */ })
    return () => ctrl.abort()
  }, [plan.slot_tertiary?.start_datetime, plan.slot_tertiary?.duration_minutes, plan.slot_tertiary?.partner_id, detail.email_id])

  // Modal-Wireup: Pencil-Click oeffnet existierenden HERO-Modal. Nach Submit
  // refresht der Plan, Backend markiert die fertigen Steps automatisch (step_enabled=false).
  const invalidateKeys: (string | number)[][] = [
    ["draft-plan", detail.email_id],
    ["draft-detail", detail.email_id],
    ["drafts"],
  ]

  const isTermin = plan.scenario === "neukunde_termin" || plan.scenario === "bestandskunde_termin"

  const badgeColor =
    plan.confidence === "high" ? { bg: "#dcfce7", fg: "#166534", border: "#86efac" } :
    plan.confidence === "medium" ? { bg: "#fef3c7", fg: "#854F0B", border: "#fcd34d" } :
    { bg: "#fee2e2", fg: "#991b1b", border: "#fca5a5" }

  const mailExcerpt = detail.original_excerpt || ""

  // Die HERO-Modals haengen an beiden Darstellungen — einmal beschrieben,
  // in beiden Rueckgabezweigen gerendert.
  const modals = (
    <>
      <CreateContactModal
        open={openModal === "contact"}
        onClose={() => setOpenModal(null)}
        mode="email"
        itemId={detail.email_id}
        onSuccess={() => setOpenModal(null)}
        prefillSalutation={plan.contact.salutation}
        prefillFirst={plan.contact.first_name}
        prefillLast={plan.contact.last_name}
        prefillPhone={plan.contact.phone}
        prefillEmail={plan.contact.email}
        prefillStreet={plan.contact.street}
        prefillZipcode={plan.contact.zipcode}
        prefillCity={plan.contact.city}
        prefillProjectName={plan.project.title}
        prefillDescription={plan.project.description}
        initialWithProject={false}
      />
      <CreateContactModal
        open={openModal === "project"}
        onClose={() => setOpenModal(null)}
        mode="email"
        itemId={detail.email_id}
        onSuccess={() => setOpenModal(null)}
        projectOnly
        // Der Kontakt haengt evtl. noch nicht an der Mail — dann legt der
        // Server ihn zusammen mit dem Projekt an (#73) und braucht diese
        // Daten. Haengt er schon dran, werden sie ignoriert.
        prefillSalutation={plan.contact.salutation}
        prefillFirst={plan.contact.first_name}
        prefillLast={plan.contact.last_name}
        prefillPhone={plan.contact.phone}
        prefillEmail={plan.contact.email}
        prefillStreet={plan.contact.street}
        prefillZipcode={plan.contact.zipcode}
        prefillCity={plan.contact.city}
        prefillProjectName={plan.project.title}
        prefillDescription={plan.project.description}
        prefillMeasureId={plan.project.measure_id}
        // `plan.project.type_id` ist der Pipeline-Step, nicht der Projekt-Typ
        // — ein historischer Name, der hier bisher in das falsche Feld lief:
        // der Typ wurde mit einer Step-ID belegt und der Status blieb leer.
        // Angelegte Projekte standen deshalb ohne Pipeline-Status in HERO.
        prefillProjectTypeId={plan.project.project_type_id}
        prefillStepId={plan.project.type_id}
        prefillPartnerId={plan.slot?.partner_id}
      />
      <ContactEditModal
        open={openModal === "contact-edit"}
        onClose={() => setOpenModal(null)}
        contactId={plan.contact.id || null}
        tenantSlug={detail.tenant_slug}
        invalidateKeys={invalidateKeys}
      />
      <SlotEditModal
        open={slotEditKey !== null}
        onClose={() => setSlotEditKey(null)}
        slot={slotEditKey ? (plan[slotEditKey as "slot" | "slot_secondary" | "slot_tertiary"] as ActionPlan["slot"]) : plan.slot}
        label={slotEditKey === "slot_secondary" ? "Termin-Vorschlag 2" : slotEditKey === "slot_tertiary" ? "Termin-Vorschlag 3" : "Termin-Vorschlag 1"}
        onSave={(next) => {
          if (!slotEditKey) return
          // slot_locked=true: der Plan ist jetzt User-gepinnt — Re-Plan und
          // refresh-stale-Slots werden ihn nicht mehr ueberschreiben.
          const nextPlan: ActionPlan = {
            ...plan,
            [slotEditKey]: next,
            slot_locked: true,
          } as ActionPlan
          update({ [slotEditKey]: next, slot_locked: true } as Partial<ActionPlan>)
          // Body via LLM mit den neuen Slot-Daten neu generieren (nur Body,
          // Slot bleibt unveraendert).
          refreshBody.mutate(nextPlan)
        }}
      />
    </>
  )

  const hatBelege = !!(plan.research?.summary || "").trim() || (plan.research?.log?.length ?? 0) > 0

  /**
   * Eine nicht geplante Aktion in den Plan aufnehmen.
   *
   * Sie kommt MIT Haken herein — der Klick IST die Zustimmung, ein zweiter
   * waere Schikane. Die Parameter sind leer; gefuellt wird darunter im
   * Detail-Block, und jeder Handler weist eine leere Aktion mit Klartext ab.
   *
   * Beim Termin geht direkt der Slot-Dialog auf: `termin_buchen` zieht seine
   * Zeit aus `plan.slot`, nicht aus `params`. Ohne diesen Sprung stuende dort
   * "Termin buchen: ?" und der Nutzer muesste raten, wo er das aendert.
   */
  const nachtragen = (key: string) => {
    if (!(plan.actions || []).some((a) => a.type === key)) {
      update({ actions: [...(plan.actions || []), { type: key, params: {} }] } as Partial<ActionPlan>)
    }
    if (key === "termin_buchen") setSlotEditKey("slot")
    if (key === "kontakt_anlegen") setOpenModal(plan.contact?.id ? "contact-edit" : "contact")
    if (key === "projekt_anlegen") setProjektEditOffen(true)
  }

  if (kompakt) {
    /** Was unter der Aktion steht und woran das "Bearbeiten" haengt. */
    const zeile = (type: string): { detail?: string; onEdit?: () => void; editLabel?: string } => {
      switch (type) {
        case "kontakt_anlegen":
        case "kontakt_korrigieren":
          return {
            detail: kontaktZeile(plan.contact),
            onEdit: editable ? () => setOpenModal(plan.contact.id ? "contact-edit" : "contact") : undefined,
            editLabel: "Bearbeiten",
          }
        case "projekt_anlegen": {
          // Weicht der Einsatzort vom Auftraggeber ab, ist das die wichtigste
          // Zeile im ganzen Plan: dorthin faehrt der Monteur.
          const ad = plan.project.address
          const abweichend = [ad?.street, ad?.zipcode, ad?.city].filter(Boolean).join(" ").trim()
          return {
            detail: projektZeile(plan.project)
              + (abweichend ? ` · Einsatzort: ${abweichend}${ad?.hinweis ? ` (${ad.hinweis})` : ""}` : ""),
            onEdit: editable ? () => setProjektEditOffen((v) => !v) : undefined,
            editLabel: projektEditOffen ? "Fertig" : "Bearbeiten",
          }
        }
        case "termin_buchen": {
          const monteur = slotCheck?.candidates?.find((c) => c.partner_id === plan.slot.partner_id)?.name
          return {
            detail: [
              plan.slot.start_datetime ? formatSlot(plan.slot.start_datetime, plan.slot.duration_minutes) : "(kein Slot erkannt)",
              monteur ? `Monteur ${monteur}` : null,
            ].filter(Boolean).join(" · "),
            onEdit: editable ? () => setSlotEditKey("slot") : undefined,
            editLabel: "Bearbeiten",
          }
        }
        case "antwort_senden":
          return {
            detail: plan.reply.subject || plan.reply.body.slice(0, 90) || "(leer)",
            onEdit: onAntwortAnsehen,
            editLabel: "Ansehen",
          }
        case "aufgabe_anlegen":
          return {
            detail: [plan.monteur_task?.title, plan.monteur_task?.target_user_name].filter(Boolean).join(" · "),
          }
        default: {
          // Aktionen aus dem Katalog-Nachtrag (07.08.2026). Ihre Daten stehen
          // nicht in eigenen Plan-Feldern, sondern in `action.params` — der
          // Grund, warum sie hier zunaechst ohne Zeile und ohne "Bearbeiten"
          // dastanden: der Nutzer sah "angebot erstellen" und sonst nichts.
          const params = (plan.actions || []).find((a) => a.type === type)?.params
          if (!params) return {}
          // KEIN `onEdit`: der Knopf sass im `<label>` der Checkbox und hakte
          // die Aktion ab, statt zu oeffnen. Bearbeitet wird unter der Zeile,
          // in `AktionDetails` — so wie das Review es macht.
          return { detail: zeigtDetailsBlock(type) ? "" : paramZeile(type, params) }
        }
      }
    }

    return (
      <div className={karte ? "ev2-pk pk-schritte" : "ev2-pk"}>
        {!karte && <div className="ev2-pk-head">
          <span className="ev2-pk-dot" />
          <span className="ev2-pk-title">{scenarioLabel(plan.scenario, plan.handling_class, plan.ohne_plan_mailart)}</span>
          <span className="ev2-pk-badge" style={{ background: badgeColor.bg, color: badgeColor.fg }}>
            {plan.confidence === "high" ? "Hohe Sicherheit" : plan.confidence === "medium" ? "Bitte prüfen" : "Unsicher"}
          </span>
          {kopfExtra && <span className="ev2-pk-head-extra">{kopfExtra}</span>}
        </div>}

        {!karte && plan.reasoning && (
          <div className="ev2-pk-reason">
            <MitProjektLinks text={ohneIds(plan.reasoning)} links={projektLinks} />
          </div>
        )}
        <Rueckfragen fragen={plan.rueckfragen} rueckfall={plan.rueckfall} />

        {/* Der Kontakt ist oft keine Aktion — er existiert ja schon. Trotzdem
            muss sichtbar sein, MIT WEM der Plan arbeitet: bei mehreren
            Treffern (Mail-Historie vs. Telefon) hat der Planer sich fuer einen
            entschieden, und das steht sonst nirgends. */}
        {plan.contact?.id && !(aktionen ?? []).some((a) => a.type.startsWith("kontakt_")) && (
          <div className="ev2-pk-infozeile">
            <span className="ev2-pk-item-title">
              Bestehender HERO-Kontakt: {kontaktName(plan.contact) || "ohne Namen"}
              {editable && (
                <button type="button" className="ev2-pk-item-edit" style={{ marginLeft: 8 }}
                  onClick={() => setOpenModal("contact-edit")}>Bearbeiten</button>
              )}
            </span>
            <span className="ev2-pk-item-detail">{kontaktZeile(plan.contact)}</span>
          </div>
        )}

        <ul className="ev2-pk-list">
          {(aktionen ?? []).map((a) => {
            const z = zeile(a.type)
            // Die weiteren Vorschlaege haengen an der Termin-Zeile: sie sind
            // keine eigene Aktion, sondern Teil derselben Buchung.
            const slotsHier = a.type === "termin_buchen" && !terminUeberholt ? (weitereSlots ?? []) : []
            return (
              <li key={a.type}>
                <label className={["ev2-pk-item", a.fertig && "fertig", !a.an && !a.fertig && "aus"].filter(Boolean).join(" ")}>
                  <input
                    type="checkbox"
                    checked={a.an}
                    disabled={a.fertig}
                    aria-label={a.label}
                    onChange={() => onToggleAktion?.(a.type)}
                  />
                  <span className="ev2-pk-item-body">
                    <span className="ev2-pk-item-title">
                      {a.familie && (
                        <span style={{
                          fontSize: 12, fontWeight: 500, color: "#5F5E5A",
                          background: "#F5F4F0", border: "0.5px solid rgba(44,44,42,0.12)",
                          borderRadius: 4, padding: "1px 5px", marginRight: 6,
                          verticalAlign: "middle",
                        }}>
                          {a.familie}
                        </span>
                      )}
                      {a.label}
                    </span>
                    {(a.detail ?? z.detail) && (
                      <span className="ev2-pk-item-detail">
                        {typeof (a.detail ?? z.detail) === "string"
                          ? <MitProjektLinks text={String(a.detail ?? z.detail)} links={projektLinks} />
                          : (a.detail ?? z.detail)}
                      </span>
                    )}
                    {(() => {
                      // Grund je Schritt (28.10.2026): woraus der Planer die
                      // Aktion ableitet — damit das Büro prüfen kann, nicht raten.
                      const grund = (plan.actions || []).find((x) => x.type === a.type)?.grund
                      return grund ? <span className="ev2-pk-item-grund"><b>Grund:</b> {grund}</span> : null
                    })()}
                    {a.zusatz && !a.fertig && <span className="ev2-pk-item-zusatz">{a.zusatz}</span>}
                  </span>
                  {z.onEdit && !a.fertig && (
                    <button
                      type="button"
                      className="ev2-pk-item-edit"
                      onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); z.onEdit?.() }}
                    >
                      {z.editLabel}
                    </button>
                  )}
                </label>
                {/* Beim Termin gehoert mehr dazu als Datum und Dauer: ist der
                    Slot noch frei, wer faehrt, und liegt dort schon ein
                    BLOCKER, den der Kunde bestaetigt hat. */}
                {a.type === "aufgabe_anlegen" && !a.fertig && plan.monteur_task?.title && (
                  <div className="ev2-pk-slot-details">
                    <AufgabeDetails
                      task={plan.monteur_task}
                      editable={editable}
                      tenantSlug={detail.tenant_slug}
                      projektLinks={projektLinks}
                      vorbelegung={{
                        contact_id: plan.contact?.id ?? null,
                        contact_name: [plan.contact?.first_name, plan.contact?.last_name].filter(Boolean).join(" "),
                        project_id: plan.project?.id ?? null,
                        project_name: plan.project?.title || "",
                      }}
                      onChange={(next) => update({
                        monteur_task: {
                          ...(plan.monteur_task || { title: "", body: "", target_user_id: null, target_user_name: "", due_date: null }),
                          ...next,
                        },
                      })}
                    />
                  </div>
                )}
                {a.type === "projekt_anlegen" && !a.fertig && projektEditOffen && editable && (
                  <div className="ev2-pk-slot-details">
                    <BestehendesProjektWahl
                      project={plan.project}
                      emailId={detail.email_id}
                      onChange={(project) => update({ project })}
                    />
                    {!plan.project.id && (
                      <ProjektDetails
                        project={plan.project}
                        emailId={detail.email_id}
                        kontaktVerknuepft={!!plan.contact?.id}
                        onChange={(patch) => update({ project: { ...plan.project, ...patch } })}
                        onFertig={() => setProjektEditOffen(false)}
                        mitFertigKnopf={false}
                      />
                    )}
                  </div>
                )}
                {zeigtDetailsBlock(a.type) && !a.fertig && (plan.actions || []).some((x) => x.type === a.type) && (
                  <div className="ev2-pk-slot-details">
                    <AktionDetails
                      type={a.type}
                      params={((plan.actions || []).find((x) => x.type === a.type)?.params || {}) as Record<string, unknown>}
                      editable={editable}
                      emailId={detail.email_id}
                      projectTypeId={plan.project?.project_type_id ?? null}
                      tenantSlug={detail.tenant_slug}
                      vorbelegung={{
                        contact_id: plan.contact?.id ?? null,
                        contact_name: [plan.contact?.first_name, plan.contact?.last_name].filter(Boolean).join(" "),
                        project_id: plan.project?.id ?? null,
                        project_name: plan.project?.title || "",
                      }}
                      onChange={(next) => update({
                        actions: (plan.actions || []).map((x) =>
                          x.type === a.type ? { ...x, params: next } : x),
                      } as Partial<ActionPlan>)}
                    />
                  </div>
                )}
                {a.type === "termin_buchen" && !a.fertig && !terminUeberholt && plan.slot.start_datetime && (
                  <div className="ev2-pk-slot-details">
                    <SlotDetails
                      slot={plan.slot}
                      slotCheck={slotCheck}
                      blockedReason={slotBlocked ? "Ohne Projekt kein Termin möglich (HERO braucht ein Projekt)." : undefined}
                      confirmed={!!plan.customer_confirmed_slot}
                      onSelectMonteur={editable ? (pid) => update({ slot: { ...plan.slot, partner_id: pid } }) : undefined}
                    />
                  </div>
                )}
                {slotsHier.map((sl, i) => {
                  const slot = sl.slot ?? plan[sl.key]
                  return (
                    <React.Fragment key={sl.key}>
                    <label
                      className={["ev2-pk-item", "ev2-pk-item-unter", !sl.an && "aus"].filter(Boolean).join(" ")}>
                      <input
                        type="checkbox"
                        checked={sl.an}
                        disabled={a.fertig || !a.an}
                        aria-label={`Terminvorschlag ${i + 2}`}
                        onChange={() => onToggleSlot?.(sl.key)}
                      />
                      <span className="ev2-pk-item-body">
                        <span className="ev2-pk-item-title">Terminvorschlag {i + 2}</span>
                        <span className="ev2-pk-item-detail">
                          {slot?.start_datetime ? formatSlot(slot.start_datetime, slot.duration_minutes) : "—"}
                        </span>
                      </span>
                      {editable && !a.fertig && (
                        <button type="button" className="ev2-pk-item-edit"
                          onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setSlotEditKey(sl.key) }}>
                          Bearbeiten
                        </button>
                      )}
                    </label>
                      {sl.an && slot?.start_datetime && (
                        <div className="ev2-pk-slot-details" style={{ marginLeft: 27 }}>
                          <SlotDetails
                            slot={slot}
                            slotCheck={sl.key === "slot_secondary" ? slotCheckSecondary : slotCheckTertiary}
                            confirmed={!!plan.customer_confirmed_slot}
                            onSelectMonteur={editable ? (pid) => update({ [sl.key]: { ...slot, partner_id: pid } } as Partial<ActionPlan>) : undefined}
                          />
                        </div>
                      )}
                    </React.Fragment>
                  )
                })}
              </li>
            )
          })}
        </ul>

        {/* 🔴 Was der Planer nicht vorgesehen hat, muss trotzdem sichtbar sein.
            Vorher zeigte die Liste ausschliesslich geplante Aktionen — wer eine
            Mail MIT Terminvorschlag vor sich hatte, fuer die der Planer keinen
            Slot fand, sah keinen Weg zu buchen und schloss daraus, es gehe
            nicht (Marlene, #12: "das kann ich wohl nicht hier?").

            Die vier Schritte mit festem Platz stehen offen, der Rest liegt
            hinter dem Aufklapper — bei einem typischen Plan waeren das sonst
            17 ausgegraute Zeilen unter zwei echten. */}
        {editable && nachtragbar && (nachtragbar.fest.length > 0 || nachtragbar.weitere.length > 0) && (
          <div className="ev2-pk-nachtrag">
            <ul className="ev2-pk-list">
              {nachtragbar.fest.map((k) => (
                <NachtragZeile key={k.key} eintrag={k} onClick={() => nachtragen(k.key)} />
              ))}
            </ul>
            {nachtragbar.weitere.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setWeitereOffen((o) => !o)}
                  style={{
                    background: "none", border: "none", padding: "6px 0",
                    color: "#888780", fontSize: 12, cursor: "pointer",
                  }}
                >
                  {weitereOffen ? "▾" : "▸"} Weitere Aktionen ({nachtragbar.weitere.length})
                </button>
                {weitereOffen && (
                  <ul className="ev2-pk-list">
                    {nachtragbar.weitere.map((k) => (
                      <NachtragZeile key={k.key} eintrag={k} onClick={() => nachtragen(k.key)} />
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {hinweis && <div className="ev2-pk-hinweis">{hinweis}</div>}

        {(primaeraktion || hatBelege || nebenaktion) && (
          <div className="ev2-pk-foot">
            {primaeraktion}
            {hatBelege && (
              <button type="button" className="ev2-pk-linkbtn" onClick={() => setBelegeOffen((v) => !v)}>
                {belegeOffen ? "Belege ausblenden" : "Woher stammt das?"}
              </button>
            )}
            {nebenaktion && <span style={{ marginLeft: "auto" }}>{nebenaktion}</span>}
          </div>
        )}

        {/* Die Belege liegen unter dem Strich: wer dem Plan glaubt, klickt
            ausfuehren; wer nicht, klappt auf. */}
        {belegeOffen && (
          <div className="ev2-pk-details">
            <ResearchSection research={plan.research} kompakt />
          </div>
        )}
        {modals}
      </div>
    )
  }

  return (
    <div style={{
      background: "#F5F4F0", padding: 20, borderRadius: 8,
      display: "flex", flexDirection: "column", gap: 16,
      fontSize: 13, color: "#2C2C2A", overflowY: "auto",
    }}>
      {/* Original-Mail — kompakt, "Anzeigen"-Button oeffnet Popup.
          Bei Portal-Eintraegen gibt es keine: dort steht, WARUM das Portal
          den Vorschlag gemacht hat (06.09.2026: „die Original-Mail gibt es
          ja so gar nicht, der User versteht das nicht"). */}
      {detail.is_proactive ? <ProaktivHerkunft detail={detail} /> : (
      <div>
        <SectionLabel>Original-Mail</SectionLabel>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 500 }}>{detail.sender || detail.sender_email || "—"}</div>
            {detail.customer_status && (
              <div style={{ fontSize: 12, color: "#5F5E5A", marginTop: 1 }}>{detail.customer_status}</div>
            )}
            {mailExcerpt && (
              <div style={{
                fontStyle: "italic", color: "#5F5E5A", lineHeight: 1.5, marginTop: 4,
                fontSize: 12, whiteSpace: "pre-wrap", overflow: "hidden",
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              }}>
                „{mailExcerpt.slice(0, 200)}{mailExcerpt.length > 200 ? "…" : ""}"
              </div>
            )}
          </div>
          <button type="button" onClick={() => setMailPopupOpen(true)}
            title="Original-Mail vollstaendig anzeigen"
            style={{
              background: "#fff", border: "0.5px solid rgba(44,44,42,0.18)",
              padding: "3px 8px", borderRadius: 5, fontSize: 12, color: "#3C3489",
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
              flexShrink: 0,
            }}>
            <Eye size={11} /> Anzeigen
          </button>
        </div>
      </div>
      )}
      {mailPopupOpen && (
        <OriginalMailPopup detail={detail} onClose={() => setMailPopupOpen(false)} />
      )}

      {/* Anhaenge mit "An HERO-Projekt anhaengen"-Button (rendert sich weg wenn keine da) */}
      {/* Portal-Eintraege sind keine Mails — es gibt nichts anzuhaengen, und der
          Endpunkt antwortet mit 404 (dev, 06.09.2026: „Laedt Anhaenge…" blieb stehen). */}
      {!detail.is_proactive && <AttachmentsSection emailId={detail.email_id} tenantSlug={detail.tenant_slug} />}

      {/* Scenario + Reasoning */}
      <div>
        <SectionLabel>Agent hat vorbereitet</SectionLabel>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{scenarioLabel(plan.scenario, plan.handling_class, plan.ohne_plan_mailart)}</span>
          <span style={{
            background: badgeColor.bg, color: badgeColor.fg, border: `1px solid ${badgeColor.border}`,
            padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 500,
          }}>
            {plan.confidence === "high" ? "Hoch" : plan.confidence === "medium" ? "Pruefen" : "Unsicher"}
          </span>
        </div>
        {plan.reasoning && (
          <div style={{ fontSize: 12, color: "#5F5E5A", marginTop: 4, fontStyle: "italic" }}>
            „{ohneIds(plan.reasoning)}"
          </div>
        )}
        <Rueckfragen fragen={plan.rueckfragen} rueckfall={plan.rueckfall} />
      </div>

      {/* Takt 1 — was vorab nachgeschlagen wurde. Nur Text, keine Aktion. */}
      <ResearchSection research={plan.research} />

      {/* Steps */}
      <div>
        <SectionLabel>Wird beim Bestaetigen ausgefuehrt {editable && <span style={{ textTransform: "none", fontWeight: 400, color: "#888780" }}>· Felder sind klickbar</span>}</SectionLabel>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {isTermin && (
            <>
              <ContactStep
                contact={plan.contact}
                editable={editable}
                onEdit={() => setOpenModal(plan.contact.id ? "contact-edit" : "contact")}
                enabled={stepEnabled.contact}
                onToggle={() => toggleStep("contact")}
              />
              <ProjectStep
                project={plan.project}
                editable={editable}
                onEdit={() => setProjektEditOffen((v) => !v)}
                enabled={stepEnabled.project}
                onToggle={() => toggleStep("project")}
              />
              {projektEditOffen && editable && (
                <li style={{ listStyle: "none", marginLeft: 32 }}>
                  <BestehendesProjektWahl
                    project={plan.project}
                    emailId={detail.email_id}
                    onChange={(project) => update({ project })}
                  />
                  {!plan.project.id && (
                    <ProjektDetails
                      project={plan.project}
                      emailId={detail.email_id}
                      kontaktVerknuepft={!!plan.contact?.id}
                      onChange={(patch) => update({ project: { ...plan.project, ...patch } })}
                      onFertig={() => setProjektEditOffen(false)}
                    />
                  )}
                </li>
              )}
              <SlotStep
                slot={plan.slot}
                editable={editable}
                onEdit={() => setSlotEditKey("slot")}
                slotCheck={slotCheck}
                enabled={stepEnabled.slot}
                onToggle={slotBlocked ? undefined : () => toggleStep("slot")}
                blockedReason={slotBlocked ? "Ohne Projekt kein Termin möglich (HERO benötigt project_match_id). Aktiviere Projekt-Step oder waehle ein bestehendes Projekt." : undefined}
                label={plan.customer_confirmed_slot ? "Bestaetigter Termin" : "Termin-Vorschlag 1"}
                onSelectMonteur={(pid) => update({ slot: { ...plan.slot, partner_id: pid } })}
                confirmed={!!plan.customer_confirmed_slot}
              />
              {plan.slot_secondary?.start_datetime && (
                <SlotStep
                  slot={plan.slot_secondary}
                  editable={editable}
                  onEdit={() => setSlotEditKey("slot_secondary")}
                  slotCheck={slotCheckSecondary}
                  enabled={stepEnabled.slot}
                  onToggle={undefined}
                  blockedReason={slotBlocked ? "Ohne Projekt kein Termin möglich" : undefined}
                  label="Termin-Vorschlag 2"
                  onSelectMonteur={(pid) => update({ slot_secondary: { ...plan.slot_secondary!, partner_id: pid } })}
                />
              )}
              {plan.slot_tertiary?.start_datetime && (
                <SlotStep
                  slot={plan.slot_tertiary}
                  editable={editable}
                  onEdit={() => setSlotEditKey("slot_tertiary")}
                  slotCheck={slotCheckTertiary}
                  enabled={stepEnabled.slot}
                  onToggle={undefined}
                  blockedReason={slotBlocked ? "Ohne Projekt kein Termin möglich" : undefined}
                  label="Termin-Vorschlag 3"
                  onSelectMonteur={(pid) => update({ slot_tertiary: { ...plan.slot_tertiary!, partner_id: pid } })}
                />
              )}
            </>
          )}
          {/* 🔴 Was der Planer NICHT vorgesehen hat, muss trotzdem sichtbar sein.
              Bis 21.08.2026 stand der ganze Kontakt/Projekt/Termin-Block hinter
              `isTermin` — waehlte der Planer ein anderes Szenario, verschwand er
              spurlos. Nicht ausgegraut, nicht gesperrt: weg. Genau das war
              Marlenes Meldung (#12): eine Mail MIT Terminvorschlag, aber kein
              Weg, einen Termin zu buchen, weil der Planer keinen Slot fand.
              Ausgegraut zeigt ausserdem die ENTSCHEIDUNG des Planers — sonst
              sieht man einem Plan nicht an, ob ein Termin erwogen und verworfen
              wurde oder nie zur Debatte stand.
              Gefahrlos, weil `actions_from_plan` (action_dispatch.py:398) einen
              Schritt erst dann zur Aktion macht, wenn er gefuellt ist. */}
          {!isTermin && editable && (
            <>
              <NichtVorgesehen
                icon={UserPlus}
                label="Kontakt"
                grund="Vom Planer nicht vorgesehen"
                knopf="+ Kontakt zuordnen"
                onClick={() => setOpenModal(plan.contact.id ? "contact-edit" : "contact")}
              />
              <NichtVorgesehen
                icon={Folder}
                label="Vorgang"
                grund="Vom Planer nicht vorgesehen"
                knopf="+ Vorgang zuordnen"
                onClick={() => setOpenModal("project")}
              />
              <NichtVorgesehen
                icon={Calendar}
                label="Termin"
                grund="Kein passender Slot gefunden — von Hand reservierbar"
                knopf="+ Termin reservieren"
                onClick={() => setSlotEditKey("slot")}
              />
            </>
          )}
          {/* HERO-Aufgabe an Monteur. Ohne Titel rendert die Komponente selbst
              ihren inaktiven Zustand mit "+ Aufgabe erstellen" — deshalb hier
              nicht mehr wegblenden. */}
          {(plan.monteur_task?.title || editable) && (
            <MonteurTaskStep
              task={plan.monteur_task}
              tenantSlug={detail.tenant_slug}
              detail={detail}
              editable={editable}
              enabled={stepEnabled.monteur_task}
              onToggle={() => toggleStep("monteur_task")}
              onChange={(next) => update({ monteur_task: { ...(plan.monteur_task || { title: "", body: "", target_user_id: null, target_user_name: "", due_date: null }), ...next } })}
            />
          )}
          {/* Katalog-Aktionen ohne eigenen Plan-Schritt.
              🔴 Bis 07.08.2026 wurden sie hier GAR NICHT gerendert — der
              Review zeigte nur die fuenf alten Schritte. Wer eine Mail von
              hier aus bestaetigte, sah nicht, dass ein Angebot, eine
              Wiedervorlage oder eine Weiterleitung Teil des Plans war. */}
          {(aktionen ?? [])
            .filter((a) => schrittFuerAktion(a.type) === null)
            .map((a) => {
              const params = (plan.actions || []).find((x) => x.type === a.type)?.params
              return (
                <StepRow
                  key={a.type}
                  icon={ListChecks}
                  enabled={a.an && !a.fertig}
                  onToggle={a.fertig ? undefined : () => onToggleAktion?.(a.type)}
                >
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#2C2C2A" }}>{a.label}</div>
                  {params && zeigtDetailsBlock(a.type) ? (
                    <div style={{ marginTop: 3, fontSize: 12 }}>
                      <AktionDetails
                        type={a.type}
                        params={params as Record<string, unknown>}
                        editable={editable && a.an && !a.fertig}
                        emailId={detail.email_id}
                        projectTypeId={plan.project?.project_type_id ?? null}
                        tenantSlug={detail.tenant_slug}
                        vorbelegung={{
                          contact_id: plan.contact?.id ?? null,
                          contact_name: [plan.contact?.first_name, plan.contact?.last_name].filter(Boolean).join(" "),
                          project_id: plan.project?.id ?? null,
                          project_name: plan.project?.title || "",
                        }}
                        onChange={(next) => update({
                          actions: (plan.actions || []).map((x) =>
                            x.type === a.type ? { ...x, params: next } : x),
                        } as Partial<ActionPlan>)}
                      />
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#5F5E5A", marginTop: 2 }}>
                      {a.detail || (params ? paramZeile(a.type, params) : "") || "—"}
                    </div>
                  )}
                </StepRow>
              )
            })}
          <Step
            icon={Mail}
            label={plan.handling_class === "aufgabe" ? "Zwischenantwort an Kunde (optional)" : "Antwort-Mail"}
            detail={plan.reply.subject || plan.reply.body.slice(0, 80) || "(leer)"}
            enabled={stepEnabled.reply}
            onToggle={() => toggleStep("reply")}
          />
        </ul>
        {/* Der ganze Katalog, klein gehalten (Paul, 06.09.2026): „nur damit
            man die Moeglichkeit hat". Der Posteingang zeigt dieselben
            Eintraege als ausgegraute Zeilen; hier reicht ein Dropdown. */}
        {editable && nachtragbar && (nachtragbar.fest.length + nachtragbar.weitere.length) > 0 && (
          <div style={{ marginTop: 10 }}>
            <select
              value=""
              aria-label="Weitere Aktion aus dem Katalog hinzufuegen"
              onChange={(e) => { if (e.target.value) nachtragen(e.target.value) }}
              style={{
                fontSize: 12, color: "#3C3489", background: "#fff",
                border: "0.5px solid rgba(60,52,137,0.4)", borderRadius: 6, padding: "4px 8px",
                maxWidth: "100%",
              }}
            >
              <option value="">+ Weitere Aktion aus dem Katalog…</option>
              {[...nachtragbar.fest, ...nachtragbar.weitere].map((k) => (
                <option key={k.key} value={k.key}>{k.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* HERO-Modals — wiederverwendet aus Email-Detail. Nach Submit refresht Plan. */}
      {modals}
    </div>
  )
}

/**
 * Rückfragen des Sol-Planers: was vor dem Bestätigen zu klären ist (z. B.
 * gleichnamiger Kontakt mit anderer Mailadresse). Rendert sich weg, wenn leer.
 */
function Rueckfragen({ fragen, rueckfall }: { fragen?: string[]; rueckfall?: string | null }) {
  const liste = (fragen || []).filter((f) => (f || "").trim())
  const vermerk = rueckfall ? (
    <div style={{ fontSize: 12, color: "#5F5E5A", margin: "4px 0" }}>{ohneIds(rueckfall)}</div>
  ) : null
  if (liste.length === 0) return vermerk
  return (
    <>
    {vermerk}
    <div role="note" aria-label="Bitte klären" style={{
      margin: "6px 0", padding: "6px 10px", borderRadius: 6, fontSize: 12.5,
      background: "#FAEEDA", color: "#633806", border: "0.5px solid rgba(99,56,6,0.25)",
    }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>Bitte klären</div>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {liste.map((f, i) => <li key={i}>{ohneIds(f)}</li>)}
      </ul>
    </div>
    </>
  )
}

/**
 * Takt 1 "Ermitteln" — was der Agent vor dem Planen in HERO nachgeschlagen
 * hat. Bewusst reine Anzeige: hier haengt keine Aktion dran (Schritt A).
 * Rendert sich weg, wenn nichts ermittelt wurde — etwa weil das Feature
 * abgeschaltet ist oder der Plan aelter ist.
 */
function ResearchSection({ research, kompakt = false }: {
  research: ActionPlan["research"]
  /** Flach: Label + Fliesstext, keine eigene Box in der Karte. */
  kompakt?: boolean
}) {
  const [showLog, setShowLog] = useState(false)
  const summary = (research?.summary || "").trim()
  const log = research?.log || []
  if (!summary && log.length === 0) return null

  if (kompakt) {
    return (
      <div className="ev2-pk-beleg">
        {summary && (
          <>
            <div className="ev2-pk-beleg-label">Geprüft</div>
            <div className="ev2-pk-beleg-text">{markdownStrippen(summary)}</div>
          </>
        )}
        {log.length > 0 && (
          <>
            <button type="button" className="ev2-pk-linkbtn"
              style={{ fontSize: 12, marginTop: summary ? 10 : 0 }}
              onClick={() => setShowLog((v) => !v)}>
              {showLog ? "Abfragen ausblenden" : `Abfragen anzeigen (${log.length})`}
            </button>
            {showLog && (
              <ul className="ev2-pk-beleg-log">
                {log.map((e, i) => (
                  <li key={i}>
                    <span className="ev2-pk-beleg-tool">{e.tool}</span>
                    <span> ({Object.values(e.input || {}).join(", ")})</span>
                    <div>{e.result}</div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      <SectionLabel>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <Search size={10} /> Vorab nachgeschlagen
          {log.length > 0 && (
            <span style={{
              background: "#fff", border: "0.5px solid rgba(44,44,42,0.15)",
              borderRadius: 999, padding: "0 6px", fontSize: 12, letterSpacing: 0,
            }}>
              {log.length}
            </span>
          )}
        </span>
      </SectionLabel>
      {summary && (
        <div style={{
          fontSize: 12, color: "#2C2C2A", lineHeight: 1.55, whiteSpace: "pre-wrap",
          background: "#fff", border: "0.5px solid rgba(44,44,42,0.12)",
          borderLeft: "3px solid #3C3489",
          borderRadius: 6, padding: "9px 11px",
        }}>
          {summary}
        </div>
      )}
      {log.length > 0 && (
        <>
          {/* Die Abfragen sind der Beleg. Wer dem Befund nicht traut, soll
              nachsehen koennen, worauf er beruht — deshalb ausklappbar und
              nicht bloss eine Fussnote. */}
          <button type="button" onClick={() => setShowLog((v) => !v)}
            style={{
              background: "none", border: "none", padding: "5px 0 0", cursor: "pointer",
              fontSize: 12, color: "#3C3489",
            }}>
            {showLog ? "Abfragen ausblenden" : "Woher stammt das?"}
          </button>
          {showLog && (
            <ul style={{
              listStyle: "none", padding: 0, margin: "5px 0 0",
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {log.map((e, i) => (
                <li key={i} style={{
                  fontSize: 12, color: "#5F5E5A", lineHeight: 1.45,
                  background: "rgba(44,44,42,0.03)", borderRadius: 6, padding: "6px 8px",
                }}>
                  <span style={{ fontFamily: "ui-monospace, monospace", color: "#3C3489" }}>
                    {e.tool}
                  </span>
                  <span style={{ color: "#888780" }}>
                    {" "}({Object.values(e.input || {}).join(", ")})
                  </span>
                  <div style={{ whiteSpace: "pre-wrap", color: "#888780", marginTop: 3 }}>
                    {e.result}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5,
      color: "#888780", marginBottom: 8, fontWeight: 500,
    }}>
      {children}
    </div>
  )
}

/**
 * Ein Schritt, den der Planer NICHT vorgesehen hat.
 *
 * Ausgegraut statt weggelassen — aus zwei Gruenden. Erstens: wer eine Moeglich-
 * keit nicht sieht, glaubt, es gebe sie nicht (Marlene, #12: "das kann ich wohl
 * nicht hier?"). Zweitens: die Zeile zeigt eine Entscheidung. Steht "Termin —
 * kein passender Slot gefunden", weiss man, dass der Planer gesucht und nichts
 * gefunden hat. Fehlt die Zeile, kann man das nicht von "nie erwogen"
 * unterscheiden.
 *
 * Bewusst KEIN Haken: ein leerer Schritt darf nicht mitlaufen, nur weil er
 * sichtbar ist. Der Knopf oeffnet den passenden Dialog, und erst der gefuellte
 * Schritt wird zur Aktion.
 */
/**
 * Eine ausgegraute Zeile im kompakten Modus: Aktion, die der Plan nicht
 * vorsieht. Klick nimmt sie auf.
 *
 * Bewusst ein Knopf und kein Kaestchen: ein leeres Kaestchen neben einem
 * gefuellten sieht aus wie "abgewaehlt", und abgewaehlt heisst "war
 * vorgesehen, will ich nicht". Das Gegenteil ist gemeint.
 */
function NachtragZeile({ eintrag, onClick }: { eintrag: CatalogEntry; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        title={eintrag.description || undefined}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          background: "none", border: "none", padding: "5px 0",
          textAlign: "left", cursor: "pointer", opacity: 0.6,
          fontSize: 12, color: "#2C2C2A",
        }}
      >
        <span style={{ color: "#888780", fontSize: 13, lineHeight: 1 }}>+</span>
        <span>{eintrag.label}</span>
        {eintrag.risk === "external" && (
          <span style={{ fontSize: 12, color: "#B45309" }}>nach aussen</span>
        )}
      </button>
    </li>
  )
}


function NichtVorgesehen({
  icon: Icon, label, grund, knopf, onClick,
}: {
  icon: React.ComponentType<{ size?: number; color?: string }>
  label: string
  grund: string
  knopf: string
  onClick: () => void
}) {
  return (
    <li style={{ display: "flex", gap: 10, alignItems: "flex-start", opacity: 0.7 }}>
      <div style={{
        width: 22, height: 22, borderRadius: 6,
        background: "#fff", border: "0.5px solid rgba(44,44,42,0.25)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Icon size={13} color="#888780" />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: "#2C2C2A" }}>{label}</div>
        <div style={{ fontSize: 12, color: "#888780", marginTop: 1 }}>{grund}</div>
        <button
          type="button"
          onClick={onClick}
          style={{
            marginTop: 4,
            background: "transparent", border: "0.5px solid #3C3489",
            color: "#3C3489", padding: "3px 10px", borderRadius: 6,
            fontSize: 12, fontWeight: 500, cursor: "pointer",
          }}
        >
          {knopf}
        </button>
      </div>
    </li>
  )
}


function StepRow({
  icon: Icon, children, enabled = true, onToggle,
}: {
  icon: any; children: React.ReactNode
  enabled?: boolean; onToggle?: () => void
}) {
  return (
    <li style={{ display: "flex", gap: 10, alignItems: "flex-start", opacity: enabled ? 1 : 0.45 }}>
      <button
        type="button"
        onClick={onToggle}
        title={onToggle ? (enabled ? "Diesen Schritt überspringen" : "Schritt aktivieren") : undefined}
        style={{
          width: 22, height: 22, borderRadius: 6,
          background: enabled ? "#3C3489" : "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, border: enabled ? "0.5px solid #3C3489" : "0.5px solid rgba(44,44,42,0.25)",
          cursor: onToggle ? "pointer" : "default", padding: 0,
        }}>
        <Icon size={13} color={enabled ? "#fff" : "#888780"} />
      </button>
      <div style={{ minWidth: 0, flex: 1, textDecoration: enabled ? "none" : "line-through" }}>{children}</div>
    </li>
  )
}

function Step({
  icon: Icon, label, detail, onEdit, enabled = true, onToggle,
}: {
  icon: any; label: string; detail: string; onEdit?: () => void
  enabled?: boolean; onToggle?: () => void
}) {
  return (
    <StepRow icon={Icon} enabled={enabled} onToggle={onToggle}>
      <div style={{ fontSize: 12, fontWeight: 500, color: "#2C2C2A", display: "flex", alignItems: "center", gap: 6 }}>
        {label}
        {onEdit && enabled && <EditBtn onClick={onEdit} />}
      </div>
      <div style={{
        fontSize: 12, color: "#5F5E5A", marginTop: 1,
        overflow: "hidden", textOverflow: "ellipsis",
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
      }}>
        {detail}
      </div>
    </StepRow>
  )
}

function EditBtn({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      title="Bearbeiten"
      style={{
        background: "none", border: "none", padding: 2, cursor: "pointer",
        color: "#888780", display: "inline-flex", alignItems: "center",
      }}>
      <Pencil size={11} />
    </button>
  )
}

/** Kontakt-Einzeiler — genutzt vom Review-Step und von der kompakten Karte. */
/** Nur der Name — fuer Ueberschriften, in denen frueher die ID stand. */
function kontaktName(contact: ActionPlan["contact"] | undefined): string {
  if (!contact) return ""
  return [contact.salutation, contact.first_name, contact.last_name].filter(Boolean).join(" ").trim()
}

function kontaktZeile(contact: ActionPlan["contact"]): string {
  const fullName = [contact.salutation, contact.first_name, contact.last_name].filter(Boolean).join(" ")
  const addr = [contact.street, [contact.zipcode, contact.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")
  return [fullName || "—", addr, contact.phone, contact.email].filter(Boolean).join(" · ")
}

/** Projekt-Einzeiler — dito. */
function projektZeile(project: ActionPlan["project"]): string {
  // #140: Mit gesetzter ID wird nichts angelegt, sondern verknuepft.
  if (project.id) return `Bestehendes Projekt verknüpfen: ${project.title || "(ohne Titel)"}`
  const projMeta: string[] = []
  if (project.measure_name) projMeta.push(project.measure_name)
  if (project.type_name) projMeta.push(project.type_name)
  return [project.title || project.description || "—", ...projMeta].join(" · ")
}

// ── Step: Kontakt (Pencil oeffnet HERO-Modal) ────────────────────────
function ContactStep({
  contact, editable, onEdit, enabled = true, onToggle,
}: {
  contact: ActionPlan["contact"]; editable: boolean
  onEdit: () => void
  enabled?: boolean; onToggle?: () => void
}) {
  const detailLine = kontaktZeile(contact)

  if (contact.id) {
    return (
      <Step icon={UserCheck} label={`Bestehender HERO-Kontakt: ${kontaktName(contact) || "ohne Namen"}`}
        detail={detailLine}
        onEdit={editable && enabled ? onEdit : undefined}
        enabled={enabled}
        onToggle={onToggle}
      />
    )
  }
  return (
    <Step icon={UserPlus} label="Neuer HERO-Kontakt"
      detail={detailLine}
      onEdit={editable && enabled ? onEdit : undefined}
      enabled={enabled}
      onToggle={onToggle}
    />
  )
}

// ── Step: Projekt (Pencil oeffnet HERO-Modal) ────────────────────────
function ProjectStep({
  project, editable, onEdit, enabled = true, onToggle,
}: {
  project: ActionPlan["project"]; editable: boolean
  onEdit: () => void
  enabled?: boolean; onToggle?: () => void
}) {
  const projMeta: string[] = []
  if (project.measure_name) projMeta.push(`Gewerk: ${project.measure_name}`)
  if (project.type_name) projMeta.push(`Typ: ${project.type_name}`)
  const detailLine = [project.title || project.description || "—", ...projMeta].join(" · ")

  if (project.id) {
    return (
      <Step icon={FolderCheck} label={`Bestehendes HERO-Projekt${project.title ? `: ${project.title}` : ""}`}
        detail={project.title || project.description || "(Folgetermin)"}
        onEdit={editable && enabled ? onEdit : undefined}
        enabled={enabled}
        onToggle={onToggle}
      />
    )
  }
  return (
    <Step icon={Folder} label="Neues HERO-Projekt"
      detail={detailLine}
      onEdit={editable && enabled ? onEdit : undefined}
      enabled={enabled}
      onToggle={onToggle}
    />
  )
}

// ── Step: Slot (Pencil oeffnet BookingModal) ─────────────────────────
function SlotStep({
  slot, editable, onEdit, slotCheck, enabled = true, onToggle, blockedReason, label, onSelectMonteur, confirmed = false,
}: {
  slot: ActionPlan["slot"]; editable: boolean
  onEdit: () => void
  slotCheck: SlotCheck | null
  enabled?: boolean; onToggle?: () => void
  blockedReason?: string
  label?: string
  onSelectMonteur?: (partner_id: number) => void
  /** customer_confirmed_slot — bestehender Termin wird bestaetigt, "belegt"-Warning unterdruecken */
  confirmed?: boolean
}) {
  const detailLine = slot.start_datetime
    ? formatSlot(slot.start_datetime, slot.duration_minutes)
    : "(kein Slot erkannt)"
  return (
    <StepRow icon={Calendar} enabled={enabled} onToggle={onToggle}>
      <div style={{ fontSize: 12, fontWeight: 500, color: "#2C2C2A", display: "flex", alignItems: "center", gap: 6 }}>
        {label || "Termin"}
        {editable && enabled && <EditBtn onClick={onEdit} />}
      </div>
      <div style={{ fontSize: 12, color: "#5F5E5A", marginTop: 1 }}>{detailLine}</div>
      {/*
        Die Termin-Beschreibung ist das, was der Monteur im Kalender liest —
        Adressen, Etage, Zugang. Sie stand frueher in der Aufgabe und war hier
        gar nicht sichtbar; wer den Plan bestaetigt, muss aber sehen, was beim
        Monteur ankommt.
      */}
      {slot.beschreibung?.trim() && (
        <div style={{
          fontSize: 12, color: "#5F5E5A", marginTop: 4, whiteSpace: "pre-wrap",
          background: "#FAFAF8", border: "1px solid #EAE9E4", borderRadius: 6,
          padding: "6px 8px",
        }}>
          <span style={{ fontWeight: 500, color: "#2C2C2A" }}>Fuer den Monteur: </span>
          {slot.beschreibung.trim()}
        </div>
      )}
      <SlotDetails
        slot={slot}
        slotCheck={slotCheck}
        blockedReason={blockedReason}
        confirmed={confirmed}
        onSelectMonteur={onSelectMonteur}
      />
    </StepRow>
  )
}

/**
 * Ein Eingabefeld, dessen Cursor beim Tippen stehen bleibt.
 *
 * 🔴 Das Problem (gemeldet 11.08.2026): Wer im Aufgaben-Text vorne etwas
 * ergaenzen wollte, landete nach jedem Zeichen wieder am Ende.
 *
 * Ursache ist die Kette hinter `onChange`: der Wert wandert per
 * `onPlanChange` in den React-Query-Cache, kommt als neues Plan-Objekt
 * zurueck und wird dem Feld wieder zugewiesen. Zwischen Tastendruck und
 * Rueckweg liegt mindestens ein Render mit dem alten Wert — und ein
 * kontrolliertes Feld setzt bei jeder Wertzuweisung die Cursorposition ans
 * Ende.
 *
 * Loesung: Das Feld haelt seinen Wert selbst und meldet ihn weiterhin bei
 * jedem Zeichen nach oben (nichts geht verloren, auch nicht beim direkten
 * Klick auf „Ausfuehren"). Von aussen wird nur uebernommen, was NICHT vom
 * eigenen Tippen stammt — etwa wenn der Planer den Text neu schreibt.
 */
export function StabilesFeld({
  wert, onChange, mehrzeilig, ...rest
}: {
  wert: string
  onChange: (v: string) => void
  mehrzeilig?: boolean
  placeholder?: string
  rows?: number
  maxLength?: number
  style?: React.CSSProperties
}) {
  const [lokal, setLokal] = useState(wert)
  const zuletztGemeldet = useRef(wert)

  useEffect(() => {
    // Nur uebernehmen, wenn die Aenderung von aussen kommt. Der Rueckweg des
    // eigenen Tippens traegt genau den Wert, den wir zuletzt gemeldet haben.
    if (wert !== zuletztGemeldet.current) {
      zuletztGemeldet.current = wert
      setLokal(wert)
    }
  }, [wert])

  const aendern = (v: string) => {
    setLokal(v)
    zuletztGemeldet.current = v
    onChange(v)
  }

  return mehrzeilig ? (
    <textarea {...rest} value={lokal} onChange={(e) => aendern(e.target.value)} />
  ) : (
    <input {...rest} value={lokal} onChange={(e) => aendern(e.target.value)} />
  )
}


function MonteurTaskStep({
  task, tenantSlug, detail, editable, enabled, onToggle, onChange,
}: {
  task: ActionPlan["monteur_task"]
  tenantSlug: string
  detail: DraftDetail
  editable: boolean
  enabled: boolean
  onToggle: () => void
  onChange: (next: Partial<ActionPlan["monteur_task"]>) => void
}) {
  const monteure = useQuery({
    queryKey: ["monteure", tenantSlug],
    queryFn: async () => {
      const r = await fetch(`/api/v1/calendar/monteure?tenant=${encodeURIComponent(tenantSlug)}`, { credentials: "include" })
      if (!r.ok) return { items: [] as { id: number; hero_user_id: number | null; name: string; main?: boolean }[] }
      return r.json() as Promise<{ items: { id: number; hero_user_id: number | null; name: string; main?: boolean }[] }>
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!tenantSlug,
  })
  const items = monteure.data?.items || []
  const currentId = task?.target_user_id ?? null
  const currentName = items.find((m) => m.hero_user_id === currentId)?.name || task?.target_user_name || ""
  const hasTitle = !!(task?.title || "").trim()

  // "Aufgabe erstellen"-Button: Title + Body aus Mail vorbefuellen, Empfaenger
  // = erster Monteur mit hero_user_id (Tenant-Default), User kann aendern.
  const handleCreate = () => {
    const sender = detail.sender || detail.sender_email || ""
    const subject = detail.subject || "Anfrage klaeren"
    const body = (detail.original_body || detail.original_excerpt || "").trim().slice(0, 500)
    const defaultMonteur = items.find((m) => m.hero_user_id != null)
    onChange({
      title: subject.slice(0, 80),
      body: body ? `Mail von ${sender}:\n\n${body}` : `Mail von ${sender} — siehe Original im Review.`,
      target_user_id: defaultMonteur?.hero_user_id ?? null,
      target_user_name: defaultMonteur?.name ?? "",
    })
    if (!enabled) onToggle()
  }

  // 🔴 MUSS vor jedem bedingten return stehen. Stand er darunter, liefen im
  // inaktiven Zustand zwei Hooks und im aktiven drei — React ordnet sie dann
  // beim Wechsel falsch zu.
  const [editing, setEditing] = useState(false)

  if (!hasTitle) {
    // Inaktiver Zustand: Button zum Erstellen
    return (
      <li style={{ display: "flex", gap: 10, alignItems: "flex-start", opacity: 0.7 }}>
        <div style={{
          width: 22, height: 22, borderRadius: 6,
          background: "#fff", border: "0.5px solid rgba(44,44,42,0.25)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <ClipboardList size={13} color="#888780" />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "#2C2C2A" }}>
            Aufgabe an Monteur
          </div>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!editable}
            style={{
              marginTop: 4,
              background: "transparent", border: "0.5px solid #3C3489",
              color: "#3C3489", padding: "3px 10px", borderRadius: 6,
              fontSize: 12, fontWeight: 500, cursor: editable ? "pointer" : "not-allowed",
            }}
            title="Erstellt eine HERO-Aufgabe mit Mail-Inhalt — User editierbar"
          >
            + Aufgabe erstellen
          </button>
        </div>
      </li>
    )
  }

  // Editier-Modus: Title/Body/Faelligkeit als Inputs. Toggle ueber den Pencil
  // rechts neben dem Header — analog zu Slot/Contact/Project-Edit.
  const inputCls = {
    width: "100%", fontSize: 12, padding: "4px 8px", border: "0.5px solid rgba(44,44,42,0.3)",
    borderRadius: 5, background: "#fff", color: "#2C2C2A", outline: "none",
  } as const
  return (
    <StepRow icon={ClipboardList} enabled={enabled} onToggle={onToggle}>
      <div style={{ fontSize: 12, fontWeight: 500, color: "#2C2C2A", display: "flex", alignItems: "center", gap: 6 }}>
        Aufgabe an Monteur
        {editable && enabled && (
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            style={{
              background: editing ? "#3C3489" : "transparent",
              color: editing ? "#fff" : "#3C3489",
              border: "0.5px solid #3C3489",
              padding: "1px 6px", borderRadius: 4,
              fontSize: 12, fontWeight: 500, cursor: "pointer",
            }}
            title={editing ? "Bearbeiten beenden" : "Aufgabe bearbeiten"}
          >
            {editing ? "✓ Fertig" : "✎ Bearbeiten"}
          </button>
        )}
      </div>
      {editing ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 4 }}>
          <StabilesFeld
            wert={task?.title || ""}
            onChange={(v) => onChange({ title: v })}
            placeholder="Aufgaben-Titel (kurz, max ~80 Zeichen)"
            maxLength={120}
            style={{ ...inputCls, fontWeight: 500 }}
          />
          <StabilesFeld
            mehrzeilig
            wert={task?.body || ""}
            onChange={(v) => onChange({ body: v })}
            placeholder="Beschreibung / Mail-Auszug"
            rows={4}
            style={{ ...inputCls, fontFamily: "inherit", resize: "vertical", whiteSpace: "pre-wrap" }}
          />
        </div>
      ) : (
        <>
          {task?.title && (
            <div style={{ fontSize: 12, color: "#2C2C2A", marginTop: 2, fontWeight: 500 }}>
              {task.title}
            </div>
          )}
          {task?.body && (
            <div style={{
              fontSize: 12, color: "#5F5E5A", marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
              whiteSpace: "pre-wrap",
            }}>
              {task.body}
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#5F5E5A" }}>👷 Empfaenger:</span>
        {editable ? (
          <select
            value={currentId ?? ""}
            onChange={(e) => {
              const val = e.target.value ? parseInt(e.target.value) : null
              const name = items.find((m) => m.hero_user_id === val)?.name || ""
              onChange({ target_user_id: val, target_user_name: name })
            }}
            style={{ fontSize: 12, padding: "2px 6px", height: 24, width: "auto" }}
          >
            <option value="">— Monteur waehlen —</option>
            {items.filter((m) => m.hero_user_id != null).map((m) => (
              <option key={m.id} value={m.hero_user_id!}>{m.name}</option>
            ))}
          </select>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 500, color: "#3C3489" }}>
            {currentName || "(keiner)"}
          </span>
        )}
        <span style={{ fontSize: 12, color: "#5F5E5A", marginLeft: 4 }}>📅 Faellig:</span>
        {editable ? (
          <input
            type="date"
            value={task?.due_date || ""}
            onChange={(e) => onChange({ due_date: e.target.value || null })}
            style={{ fontSize: 12, padding: "2px 6px", height: 24, border: "0.5px solid rgba(44,44,42,0.3)", borderRadius: 4 }}
          />
        ) : (
          <span style={{ fontSize: 12, fontWeight: 500, color: "#3C3489" }}>
            {task?.due_date || "(offen)"}
          </span>
        )}
      </div>
    </StepRow>
  )
}

/**
 * Was in der Aufgabe steht — Beschreibung, Faelligkeit, und beides aenderbar.
 *
 * Im Posteingang stand lange nur der Titel. Der Text ist aber das, was der
 * Monteur spaeter liest: bei "Einsatzort: Mieterin Gerda Lange, nicht
 * Auftraggeber" steckt die eigentliche Information im Body, nicht in der
 * Ueberschrift.
 */

/**
 * Projekt-Daten des Plans bearbeiten — direkt unter der Zeile, OHNE HERO.
 *
 * Geschrieben wird in `plan.project`; angelegt wird der Vorgang erst beim
 * Ausfuehren, und zwar nach dem Kontakt (der Executor haelt die Reihenfolge).
 * Marlene, 08.09.2026 (#84): „Bei neuen Kontakten kann man ebenfalls nicht
 * die Projektbeschreibung aendern, da die Fehlermeldung kommt, der Kontakt
 * muesste angelegt werden. Das sollte in einem funktionieren."
 *
 * Gewerk und Projekttyp kommen aus demselben Optionen-Endpunkt wie die
 * HERO-Dialoge — archivierte Typen sind dort inzwischen ausgefiltert.
 */
// ── #140: Bestehendes Projekt statt neuem verknuepfen ─────────────────
// Paul, 28.09.2026: „Wo kann ich hier das bestehende Projekt verknüpfen? Sehe
// nur ‚Neues Projekt anlegen‘." Der gelbe Hinweis verwies auf „Bearbeiten",
// dort gab es aber nur Name/Gewerk/Typ fuer ein NEUES Projekt.
//
// 🔴 Schreibt NIE in HERO — nur `plan.project.id` im gespeicherten Plan. Der
// Executor nimmt ein Projekt mit ID als bestehendes (`reuse_project`).
// `type_id`/`type_name` werden geleert: mit gesetztem Step wuerde der
// Executor den Status des bestehenden Projekts umstellen — das soll ein
// Verknuepfen nicht nebenbei tun.
export type HeroProjektKurz = { id: number; code?: string | null; name?: string; title?: string; status?: string }

export function projektBezeichnung(p: HeroProjektKurz): string {
  const titel = (p.name || p.title || "").trim()
  return [p.code, titel].filter(Boolean).join(" — ") || "Projekt ohne Titel"
}

export function projektVerknuepfen(
  project: ActionPlan["project"], p: HeroProjektKurz,
): ActionPlan["project"] {
  return { ...project, id: p.id, title: projektBezeichnung(p), type_id: null, type_name: "" }
}

export function BestehendesProjektWahl({
  project, emailId, onChange,
}: {
  project: ActionPlan["project"]
  emailId: number
  onChange: (project: ActionPlan["project"]) => void
}) {
  // Gleicher Query-Key wie der Hinweis in ReviewCard — kein zweiter Abruf.
  const q = useQuery<{ projects?: HeroProjektKurz[] }>({
    queryKey: ["email-hero-projects", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-projects`, { credentials: "include" })
      if (!r.ok) return {}
      return await r.json()
    },
    staleTime: 1000 * 30,
  })
  // Was vor dem Verknuepfen im Plan stand — zurueck auf „neu" stellt es wieder her.
  const vorher = useRef<ActionPlan["project"] | null>(project.id ? null : project)
  const projekte = q.data?.projects || []
  if (q.isLoading) return <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>Projekte des Kontakts werden geladen …</div>
  if (!projekte.length && !project.id) return null
  const wert = project.id ? String(project.id) : ""
  return (
    <label style={{ display: "block", fontSize: 12, marginBottom: 10 }} data-testid="bestehendes-projekt-wahl">
      <span style={{ display: "block", color: "var(--text-muted)", marginBottom: 4 }}>Projekt</span>
      <select
        value={wert}
        onChange={(e) => {
          const id = Number(e.target.value)
          if (!id) {
            const alt = vorher.current
            onChange(alt ? { ...alt, id: null } : { ...project, id: null, title: "" })
            return
          }
          const p = projekte.find((x) => x.id === id)
          if (!p) return
          if (!project.id) vorher.current = project
          onChange(projektVerknuepfen(project, p))
        }}
        style={{ width: "100%", fontSize: 13 }}
      >
        <option value="">Neues Projekt anlegen</option>
        {projekte.map((p) => (
          <option key={p.id} value={String(p.id)}>
            Bestehendes verknüpfen: {projektBezeichnung(p)}{p.status ? ` (${p.status})` : ""}
          </option>
        ))}
        {project.id && !projekte.some((p) => p.id === project.id) && (
          <option value={String(project.id)}>{project.title || "Bestehendes Projekt"}</option>
        )}
      </select>
    </label>
  )
}

function ProjektDetails({
  project, emailId, kontaktVerknuepft, onChange, onFertig, mitFertigKnopf = true,
}: {
  project: ActionPlan["project"]
  emailId: number
  kontaktVerknuepft: boolean
  onChange: (patch: Partial<ActionPlan["project"]>) => void
  onFertig: () => void
  /** Im Posteingang steht „Fertig" schon an der Zeile — dann hier keiner. */
  mitFertigKnopf?: boolean
}) {
  const optionen = useQuery<{
    measures?: { id: number; name: string }[]
    project_types?: { id: number; name: string }[]
  }>({
    queryKey: ["hero-options", "email", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-options`, { credentials: "include" })
      if (!r.ok) return {}
      return r.json()
    },
    staleTime: 1000 * 60 * 5,
  })
  const inputCls = {
    width: "100%", fontSize: 12.5, padding: "5px 8px",
    border: "1px solid var(--border-color)", borderRadius: 6,
    background: "var(--bg-card)", color: "var(--text)", outline: "none",
  } as const
  const feld = (text: string, kind: React.ReactNode) => (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>{text}</span>
      {kind}
    </label>
  )
  const measures = optionen.data?.measures || []
  const typen = optionen.data?.project_types || []
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="projekt-details">
      {feld("Projektname", (
        <input type="text" value={project.title || ""} aria-label="Projektname"
          onChange={(e) => onChange({ title: e.target.value })} style={inputCls} />
      ))}
      {feld("Projekt-Beschreibung", (
        <textarea rows={3} value={project.description || ""} aria-label="Projekt-Beschreibung"
          onChange={(e) => onChange({ description: e.target.value })}
          style={{ ...inputCls, fontFamily: "inherit", resize: "vertical" }} />
      ))}
      {feld("Gewerk", (
        <select value={project.measure_id ?? ""} aria-label="Gewerk" style={inputCls}
          onChange={(e) => {
            const id = e.target.value ? Number(e.target.value) : null
            onChange({ measure_id: id, measure_name: measures.find((m) => m.id === id)?.name || "" })
          }}>
          {/* Der Planer liefert oft nur den NAMEN des Gewerks, keine ID — dann
              steht der Name im Platzhalter, sonst saehe es aus, als fehle er. */}
          <option value="">
            {optionen.isLoading ? "lädt …"
              : project.measure_id == null && project.measure_name ? `— Gewerk wählen — (laut Plan: ${project.measure_name})`
              : "— Gewerk wählen —"}
          </option>
          {measures.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          {/* Vorausgewaehlter Wert braucht seinen eigenen <option>, sonst zeigt
              das Feld waehrend des Ladens den Platzhalter. */}
          {project.measure_id != null && !measures.some((m) => m.id === project.measure_id) && (
            <option value={project.measure_id}>{project.measure_name || `Gewerk ${project.measure_id}`}</option>
          )}
        </select>
      ))}
      {feld("Projekttyp", (
        <select value={project.project_type_id ?? ""} aria-label="Projekttyp" style={inputCls}
          onChange={(e) => {
            const id = e.target.value ? Number(e.target.value) : null
            onChange({ project_type_id: id, project_type_name: typen.find((t) => t.id === id)?.name || "" })
          }}>
          <option value="">
            {optionen.isLoading ? "lädt …"
              : project.project_type_id == null && (project.project_type_name || project.type_name)
                ? `— Projekttyp wählen — (laut Plan: ${project.project_type_name || project.type_name})`
              : "— Projekttyp wählen —"}
          </option>
          {typen.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          {project.project_type_id != null && !typen.some((t) => t.id === project.project_type_id) && (
            <option value={project.project_type_id}>{project.project_type_name || `Typ ${project.project_type_id}`}</option>
          )}
        </select>
      ))}
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-muted)" }}>
        <span>
          {kontaktVerknuepft
            ? "Wird beim Ausführen als Vorgang am verknüpften Kontakt angelegt."
            : "Wird beim Ausführen angelegt — zuerst der Kontakt, dann der Vorgang."}
        </span>
        {mitFertigKnopf && (
          <button type="button" className="ev2-pk-linkbtn" style={{ marginLeft: "auto" }} onClick={onFertig}>Fertig</button>
        )}
      </div>
    </div>
  )
}

/**
 * Bearbeiten einer Katalog-Aktion — direkt unter der Zeile.
 *
 * 🔴 Der erste Anlauf hatte den „Bearbeiten"-Knopf INNERHALB des `<label>`,
 * das die Checkbox umschliesst. Ein Klick darauf hakte die Aktion ab, statt
 * zu oeffnen: ein Label leitet Klicks an sein Steuerelement weiter. Das
 * Review macht es seit jeher richtig — `AufgabeDetails` sitzt unter der
 * Zeile, ausserhalb des Labels, mit eigenem Knopf. Genau das hier auch.
 *
 * Die Felder kommen aus `AKTION_FELDER`; geschrieben wird in
 * `plan.actions[i].params` — die Struktur, die der Verteiler ausfuehrt.
 */
function AktionDetails({
  type, params, editable, onChange, emailId, projectTypeId, tenantSlug, vorbelegung,
}: {
  type: string
  params: Record<string, unknown>
  editable: boolean
  onChange: (next: Record<string, unknown>) => void
  /** Fuer die HERO-Optionen (Pipeline-Schritte). */
  emailId: number
  /** Projekt-Typ des betroffenen Vorgangs — bestimmt, welche Schritte es gibt. */
  projectTypeId?: number | null
  tenantSlug: string
  /** Kontakt/Projekt der Mail — Vorbelegung fuer den Aufgaben-Dialog. */
  vorbelegung?: { contact_id?: number | null; contact_name?: string; project_id?: number | null; project_name?: string }
}) {
  const [offen, setOffen] = useState(false)
  const felder = AKTION_FELDER[type] || []

  // Mail 116596 (29.10.2026, Paul): „Projekt-Status setzen: Archiviert“ —
  // ohne Vorgang. „So kann man ja nicht überprüfen, obs richtig ist.“ Der
  // Vorgang steht jetzt davor; ältere Pläne ohne `project_label` holen die
  // Bezeichnung live aus HERO (nie die interne ID).
  // 🔴 Nicht `vorbelegung.project_name` nehmen: das ist nur der Titel
  // („Klimaanlage Beratung und Angebot“), ohne HERO-Nummer — Paul 29.10.:
  // „hier steht keine Projektbezeichnung“. Der Lookup liefert „NR — Titel“
  // und den aktuellen Status für Pläne ohne `vorher_step_name`.
  const statusPid = type === "projekt_status_setzen"
    ? (params.project_id as number | string | undefined) || vorbelegung?.project_id || null
    : null
  const ausHero = useHeroVorgang(tenantSlug, statusPid,
    !!statusPid && (!params.project_label || !params.vorher_step_name))

  // Pipeline-Schritte nur laden, wenn ein Feld sie braucht — und erst beim
  // Aufklappen. Derselbe Endpunkt, den die HERO-Dialoge schon nutzen.
  const brauchtSteps = felder.some((f) => f.art === "step")
  const heroOptionen = useQuery<{
    project_types?: { id: number; name: string; steps?: { id: number; name: string }[] }[]
  }>({
    queryKey: ["hero-options", "email", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-options`, { credentials: "include" })
      if (!r.ok) return {}
      return r.json()
    },
    staleTime: 1000 * 60 * 5,
    enabled: brauchtSteps && offen,
  })
  const typen = heroOptionen.data?.project_types || []
  // Kennen wir den Projekt-Typ, zeigen wir nur dessen Schritte — sonst alle,
  // nach Typ gruppiert. Ein Schritt aus einer fremden Pipeline waere in HERO
  // ein Fehler, kein anderer Status.
  const passende = projectTypeId ? typen.filter((t) => t.id === projectTypeId) : typen
  const inputCls = {
    width: "100%", fontSize: 12.5, padding: "5px 8px",
    border: "1px solid var(--border-color)", borderRadius: 6,
    background: "var(--bg-card)", color: "var(--text)", outline: "none",
  } as const

  const setzen = (key: string, roh: string, art?: string) => {
    const next = { ...params }
    const wert = roh.trim()
    if (!wert) {
      // Leer heisst "nicht gesetzt" — der Handler faellt dann auf seinen
      // Standard zurueck (z.B. 7 Tage Frist). Ein leerer String haette den
      // Standard ausgehebelt.
      delete next[key]
    } else {
      next[key] = art === "zahl" && !Number.isNaN(Number(wert)) ? Number(wert) : wert
    }
    onChange(next)
  }

  // 🔑 Aufgaben-Aktionen bekommen den FESTEN Aufgaben-Dialog des Portals —
  // denselben, den „Neue Aufgabe" ueberall oeffnet. Dort haengen Kontakt- und
  // Projekt-Picker dran, samt HERO-Regel „entweder ODER". Eine eigene
  // Feldliste daneben waere ein zweiter, schlechterer Dialog fuer dieselbe
  // Sache (gemeldet am 07.08.2026: „ich will das gleiche popup").
  if (type === "terminlink_senden") {
    return <TerminlinkAktion params={params} editable={editable} onChange={onChange} emailId={emailId} />
  }

  if (AUFGABEN_AKTIONEN.has(type)) {
    return (
      <>
        {/* Gemeldet am 14.08.2026 zu „Abrechnung am Vorgang vermerken":
            in einer Zeile mit Ellipse blieb unsichtbar, WAS vermerkt wird und
            wo es landet — genau die Frage, die der Nutzer an der Karte hat.
            Der Text bekommt deshalb die volle Breite und darf umbrechen; der
            Knopf rutscht bei Bedarf in die naechste Zeile. Vorher teilte er
            sich die Zeile mit dem Text, der dadurch auf ~200px schrumpfte. */}
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap",
          color: "var(--text-muted)",
        }}>
          <span style={{ flex: "1 1 100%", minWidth: 0, lineHeight: 1.35 }}>
            {paramZeile(type, params) || "—"}
          </span>
          {editable && (
            <button type="button" className="ev2-pk-item-edit" style={{ marginLeft: "auto" }}
              onClick={() => setOffen(true)}>
              Bearbeiten
            </button>
          )}
        </div>
        {/* Logbuch-Eintrag frei schreiben (#34). Bewusst hier und nicht im
            Aufgaben-Dialog: der ist der gemeinsame „Neue Aufgabe"-Dialog, und
            ein Feld, das nur diese eine Aktion braucht, gehoert nicht hinein. */}
        {FREIER_LOGBUCH_TEXT.has(type) && editable && (
          <label style={{ display: "block", marginTop: 6 }}>
            <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>
              Logbuch-Eintrag
              <span style={{ opacity: 0.8 }}> · leer = wird aus den Angaben oben zusammengesetzt</span>
            </span>
            <textarea
              rows={3}
              value={String(params.logbuch_text ?? "")}
              placeholder="Was am Vorgang stehen soll — z. B. wer welchen Teil zahlt"
              onChange={(e) => {
                const next = { ...params }
                const wert = e.target.value
                if (wert.trim()) next.logbuch_text = wert
                else delete next.logbuch_text
                onChange(next)
              }}
              style={{ ...inputCls, resize: "vertical" }}
            />
          </label>
        )}
        <TaskCreateModal
          open={offen}
          onClose={() => setOffen(false)}
          defaultTenant={tenantSlug}
          titel={`${label(type)} bearbeiten`}
          initial={{
            title: String(params.title ?? "") || vorschlagTitel(type, params),
            comment: String(params.comment ?? params.note ?? params.reason ?? params.info ?? params.hinweis ?? ""),
            due_date: String(params.due_date ?? ""),
            target_user: params.target_user_id
              ? `${params.target_user_id}|${String(params.target_user_name ?? "")}` : "",
            // Kontakt und Projekt kommen aus der Mail, wenn der Plan nichts
            // sagt — der Nutzer soll sie nicht jedes Mal neu heraussuchen.
            contact_id: Number(params.contact_id ?? vorbelegung?.contact_id ?? 0) || null,
            contact_name: String(params.contact_name ?? vorbelegung?.contact_name ?? ""),
            project_match_id: Number(params.project_match_id ?? vorbelegung?.project_id ?? 0) || null,
            project_name: String(params.project_name ?? vorbelegung?.project_name ?? ""),
          }}
          onSave={(w) => {
            const next = { ...params }
            next.title = w.title
            next.comment = w.comment
            if (w.due_date) next.due_date = w.due_date; else delete next.due_date
            const [uid, uname] = (w.target_user || "").split("|")
            if (uid) { next.target_user_id = Number(uid); next.target_user_name = uname || "" }
            else { delete next.target_user_id; delete next.target_user_name }
            if (w.contact_id) { next.contact_id = w.contact_id; next.contact_name = w.contact_name }
            else { delete next.contact_id; delete next.contact_name }
            if (w.project_match_id) { next.project_match_id = w.project_match_id; next.project_name = w.project_name }
            else { delete next.project_match_id; delete next.project_name }
            onChange(next)
          }}
        />
      </>
    )
  }

  if (offen && editable) {
    const fehlend = felder.filter((f) => f.pflicht && !String(params[f.key] ?? "").trim())
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {felder.map((f) => (
          <label key={f.key} style={{ display: "block" }}>
            <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>
              {f.label}{f.pflicht && <span style={{ color: "var(--destructive)" }}> *</span>}
              {f.hinweis && <span style={{ opacity: 0.8 }}> · {f.hinweis}</span>}
            </span>
            {f.art === "step" ? (
              <select
                value={String(params[f.key] ?? "")}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null
                  const name = passende
                    .flatMap((t) => t.steps || [])
                    .find((st) => st.id === id)?.name || ""
                  // Bezeichnung gleich mitschreiben: sie steht in der
                  // Vorschauzeile und im Bestaetigungs-Dialog.
                  const next = { ...params }
                  if (id === null) { delete next[f.key]; delete next.step_name }
                  else { next[f.key] = id; next.step_name = name }
                  onChange(next)
                }}
                style={inputCls}
              >
                <option value="">
                  {heroOptionen.isLoading ? "lädt …" : "— Status wählen —"}
                </option>
                {passende.map((t) => (
                  <optgroup key={t.id} label={t.name}>
                    {(t.steps || []).map((st) => (
                      <option key={st.id} value={st.id}>{st.name}</option>
                    ))}
                  </optgroup>
                ))}
                {/* 🔑 Ein vorausgewaehlter Wert braucht immer einen eigenen
                    <option>, sonst zeigt das Feld waehrend des Ladens oder bei
                    einem Fehler faelschlich den Platzhalter. */}
                {params[f.key] != null && !passende.flatMap((t) => t.steps || [])
                  .some((st) => st.id === Number(params[f.key])) && (
                  <option value={String(params[f.key])}>
                    {String(params.step_name || `Schritt ${params[f.key]}`)}
                  </option>
                )}
              </select>
            ) : f.art === "haken" ? (
              <input
                type="checkbox"
                aria-label={f.label}
                checked={params[f.key] === true || ["true", "ja", "1"].includes(String(params[f.key] ?? "").toLowerCase())}
                onChange={(e) => {
                  const next = { ...params }
                  if (e.target.checked) next[f.key] = true
                  else delete next[f.key]
                  onChange(next)
                }}
              />
            ) : f.art === "mehrzeilig" ? (
              <textarea
                value={String(params[f.key] ?? "")}
                rows={3}
                onChange={(e) => setzen(f.key, e.target.value, f.art)}
                style={{ ...inputCls, fontFamily: "inherit", resize: "vertical" }}
              />
            ) : (
              <input
                type={f.art === "zahl" ? "number" : "text"}
                value={String(params[f.key] ?? "")}
                onChange={(e) => setzen(f.key, e.target.value, f.art)}
                style={inputCls}
              />
            )}
          </label>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {fehlend.length > 0 && (
            <span style={{ fontSize: 12, color: "var(--destructive)" }}>
              Pflichtfeld fehlt: {fehlend.map((f) => f.label).join(", ")}
            </span>
          )}
          <button type="button" className="ev2-pk-linkbtn" style={{ marginLeft: "auto" }}
            onClick={() => setOffen(false)}>Fertig</button>
        </div>
      </div>
    )
  }

  const zeileParams = statusPid && !params.vorher_step_name && ausHero?.status
    && ausHero.status !== "Unbekannt" && ausHero.status !== params.step_name
    ? { ...params, vorher_step_name: ausHero.status }
    : params
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-muted)" }}>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {type === "projekt_status_setzen" && (
          <span style={{ color: "var(--text)", fontWeight: 500 }}>
            {statusPid ? (
              <HeroVorgangName
                tenantSlug={tenantSlug}
                projectId={statusPid}
                bekannt={String(params.project_label || "") || ausHero?.name}
              />
            ) : "Kein Vorgang verknüpft"}
            {": "}
          </span>
        )}
        {paramZeile(type, zeileParams) || "—"}
      </span>
      {editable && (
        <button type="button" className="ev2-pk-item-edit" style={{ marginLeft: "auto" }}
          onClick={() => setOffen(true)}>
          Bearbeiten
        </button>
      )}
    </div>
  )
}

/**
 * Die Monteur-Aufgabe aus dem Plan — Vorschau plus „Bearbeiten".
 *
 * 🔑 Bearbeitet wird im Aufgaben-Dialog des Portals, demselben, den die
 * uebrigen Aufgaben-Aktionen und „Neue Aufgabe" oeffnen. Die frueheren
 * Felder direkt in der Karte schrieben bei jedem Tastendruck in den
 * Plan-Cache; React baute das Feld neu auf und der Cursor sprang ans
 * Textende (gemeldet 12.08.2026). Der Dialog haelt seinen eigenen Zustand
 * und schreibt erst beim Speichern.
 *
 * Kontakt und Projekt sind mit denen des Plans vorbelegt — der Nutzer soll
 * sie nicht heraussuchen muessen. Weicht seine Wahl davon ab, landet sie in
 * `monteur_task.contact_id/project_match_id`; nur diese Aufgabe haengt dann
 * woanders, der uebrige Plan bleibt, wie er ist.
 */
function AufgabeDetails({
  task, editable, onChange, tenantSlug, vorbelegung, projektLinks,
}: {
  task: ActionPlan["monteur_task"]
  editable: boolean
  onChange: (next: Partial<ActionPlan["monteur_task"]>) => void
  tenantSlug: string
  vorbelegung: { contact_id?: number | null; contact_name?: string; project_id?: number | null; project_name?: string }
  projektLinks: ProjektLinks
}) {
  const [offen, setOffen] = useState(false)
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {task?.body && (
        <div style={{
          color: "var(--text-secondary)", lineHeight: 1.55, whiteSpace: "pre-wrap",
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
        }}>
          <MitProjektLinks text={task.body} links={projektLinks} />
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-muted)" }}>
        <span>Fällig: {task?.due_date || "offen"}</span>
        {task?.target_user_name && <span>· für {task.target_user_name}</span>}
        {editable && (
          <button type="button" className="ev2-pk-item-edit" onClick={() => setOffen(true)}>
            Bearbeiten
          </button>
        )}
      </div>
      <TaskCreateModal
        open={offen}
        onClose={() => setOffen(false)}
        defaultTenant={tenantSlug}
        titel="Aufgabe bearbeiten"
        initial={{
          title: task?.title || "",
          comment: task?.body || "",
          due_date: task?.due_date || "",
          target_user: task?.target_user_id
            ? `${task.target_user_id}|${task.target_user_name || ""}` : "",
          contact_id: task?.contact_id ?? vorbelegung.contact_id ?? null,
          contact_name: vorbelegung.contact_name || "",
          project_match_id: task?.project_match_id ?? vorbelegung.project_id ?? null,
          project_name: vorbelegung.project_name || "",
        }}
        onSave={(w) => {
          const [uid, uname] = (w.target_user || "").split("|")
          onChange({
            title: w.title,
            body: w.comment,
            due_date: w.due_date || null,
            target_user_id: uid ? Number(uid) : null,
            target_user_name: uname || "",
            contact_id: w.contact_id,
            project_match_id: w.project_match_id,
          })
        }}
      />
    </div>
  )
}

function scenarioLabel(s: string, handlingClass?: string, ohnePlanMailart?: string | null): string {
  if (handlingClass === "aufgabe") return "Aufgabe an Monteur"
  // Regel aus dem Kontext-Center („Mailarten ohne Plan"): der Nutzer soll
  // sehen, dass das kein Urteil ueber diese Mail ist, sondern eine Regel —
  // und wo er sie aendert.
  if (s === "skip" && ohnePlanMailart) return `Ohne Plan laut Kontext-Center: ${ohnePlanMailart.split(":")[0].trim()}`
  return ({
    neukunde_termin: "Neukunden-Termin",
    bestandskunde_termin: "Termin-Buchung",
    reply_only: "Nur Antwort",
    skip: "Keine Aktion noetig",
  } as Record<string, string>)[s] || s
}

function formatSlot(iso: string, durationMin: number): string {
  try {
    const d = new Date(iso)
    const dateStr = d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })
    const timeStr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    return `${dateStr} ${timeStr} (${durationMin} Min)`
  } catch {
    return iso
  }
}

// ── Original-Mail Popup (modal overlay) ──────────────────────────────
export function OriginalMailPopup({ detail, onClose }: { detail: DraftDetail; onClose: () => void }) {
  // Esc schliesst
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  // Render-Quellen: HTML > Body > Excerpt. Wenn alles leer → lazy IMAP-Refetch.
  const [html, setHtml] = useState(detail.original_html || "")
  const [text, setText] = useState(detail.original_body || "")
  const [refetching, setRefetching] = useState(false)
  const [refetchErr, setRefetchErr] = useState<string | null>(null)
  const hasContent = !!(html || text || detail.original_excerpt)

  useEffect(() => {
    // Auto-Refetch wenn weder HTML noch Body da ist und wir eine Email-ID haben.
    // refetch-body holt den Body direkt aus IMAP nach (kann bei IONOS 30+ Sek
    // dauern wenn die Mail aelter ist).
    if (html || text) return
    if (!detail.email_id) return
    let cancelled = false
    setRefetching(true)
    setRefetchErr(null)
    fetch(`/api/v1/emails/${detail.email_id}/refetch-body`, {
      method: "POST", credentials: "include",
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}))
        if (cancelled) return
        if (!r.ok || !d.ok) {
          setRefetchErr(d.detail || `HTTP ${r.status}`)
        } else {
          if (d.body_html) setHtml(d.body_html)
          if (d.body_text) setText(d.body_text)
        }
      })
      .catch((e) => { if (!cancelled) setRefetchErr(String(e)) })
      .finally(() => { if (!cancelled) setRefetching(false) })
    return () => { cancelled = true }
  }, [detail.email_id, html, text])

  return (
    <div className="pp-overlay" onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 10, maxWidth: 800, width: "90%",
          maxHeight: "85vh", display: "flex", flexDirection: "column",
          boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
        }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px", borderBottom: "0.5px solid rgba(44,44,42,0.12)",
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#2C2C2A" }}>{detail.subject || "(ohne Betreff)"}</div>
            <div style={{ fontSize: 12, color: "#888780", marginTop: 2 }}>
              Von: {detail.sender || detail.sender_email || "—"}
              {detail.received_at && <> · {new Date(detail.received_at).toLocaleString("de-DE")}</>}
            </div>
          </div>
          <button type="button" onClick={onClose} title="Schliessen (Esc)"
            style={{
              background: "none", border: "none", padding: 6, cursor: "pointer",
              color: "#5F5E5A", display: "inline-flex",
            }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {html ? (
            <EmailBody html={html} minHeight={400} />
          ) : text ? (
            <div style={{
              padding: "16px 20px", fontSize: 13, lineHeight: 1.6,
              color: "#2C2C2A", whiteSpace: "pre-wrap",
            }}>{text}</div>
          ) : detail.original_excerpt ? (
            <div style={{
              padding: "16px 20px", fontSize: 13, lineHeight: 1.6,
              color: "#2C2C2A", whiteSpace: "pre-wrap",
            }}>{detail.original_excerpt}</div>
          ) : refetching ? (
            <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: "#5F5E5A" }}>
              Lade Original-Mail aus IMAP… (kann bei alten Mails 30+ Sek dauern)
            </div>
          ) : refetchErr ? (
            <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: "#991b1b" }}>
              Konnte Mail nicht aus IMAP nachladen: {refetchErr}
            </div>
          ) : (
            <div style={{ padding: 32, textAlign: "center", fontSize: 13, color: "#888780" }}>
              (kein Body verfuegbar)
            </div>
          )}
          {!hasContent && refetching && (
            <div style={{ padding: "8px 20px", fontSize: 12, color: "#888780", borderTop: "0.5px solid rgba(44,44,42,0.08)" }}>
              Tipp: Beim ersten Öffnen einer alten Mail dauert das laden. Beim nächsten Öffnen ist sie gecacht.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
